import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import styles from './Mailer.module.css';

/** Small shared pieces used across the Mailer tabs. */

/**
 * A button that owns the lifecycle of the async action it triggers.
 *
 * Every action here talks to a mail provider over the network, so "did that do
 * anything?" is a real question the UI has to answer. The button moves through
 * idle -> busy -> done/failed and reports each state itself, rather than
 * leaving the user to infer success from a notice that may have scrolled out of
 * view. It also blocks re-entry while running, which matters when the action
 * sends mail: a double click should never mean two emails.
 *
 * `onClick` is expected to throw on failure. Handlers that swallow their own
 * errors must rethrow, or the button will report a success that did not happen.
 */
export const ActionButton = ({
  onClick,
  children,
  icon,
  className = 'btn btn-ghost',
  busyLabel,
  doneLabel = 'Done',
  failLabel = 'Failed',
  disabled,
  title,
  style,
}) => {
  const [state, setState] = useState('idle');
  const timer = useRef(null);
  const mounted = useRef(true);

  // `mounted` must be re-armed on every mount, not just initialised once.
  //
  // StrictMode mounts, runs the cleanup, then runs the effect again. With only
  // the cleanup touching this ref, the flag was left `false` after that second
  // pass and stayed false for the component's whole life — so `settle()` bailed
  // out every time and the button span on "Saving…" forever, even though the
  // request had succeeded. The work was fine; only the badge lied.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const settle = (next, ms) => {
    if (!mounted.current) return;
    setState(next);
    timer.current = setTimeout(() => mounted.current && setState('idle'), ms);
  };

  const run = async (event) => {
    if (state === 'busy' || disabled) return;
    setState('busy');
    try {
      await onClick?.(event);
      settle('done', 2200);
    } catch {
      // The error text itself is surfaced by the caller's toast; the button
      // only has to show that this particular action is what failed.
      settle('failed', 3200);
    }
  };

  const label = state === 'busy' ? (busyLabel || children)
    : state === 'done' ? doneLabel
      : state === 'failed' ? failLabel
        : children;

  return (
    <button
      className={`${className} ${state === 'failed' ? styles.actionFailed : ''} ${state === 'done' ? styles.actionDone : ''}`}
      onClick={run}
      disabled={disabled || state === 'busy'}
      title={title}
      style={style}
      type="button"
    >
      {state === 'busy' && <span className="spinner" style={{ width: 15, height: 15 }} />}
      {state === 'done' && <Check size={16} />}
      {state === 'failed' && <AlertCircle size={16} />}
      {state === 'idle' && icon}
      {label}
    </button>
  );
};

/**
 * Fixed-position feedback, so the result of an action is visible wherever the
 * page happens to be scrolled. The inline boxes this replaces sat at the top of
 * a long form and were routinely missed.
 */
export const Toast = ({ message, tone = 'info', onDismiss }) => {
  useEffect(() => {
    if (!message) return undefined;
    // A warning has to outlast a success: it is usually a sentence of
    // consequence that has to be read, not an acknowledgement to be glanced at.
    const linger = tone === 'error' ? 8000 : tone === 'warn' ? 9000 : 4500;
    const t = setTimeout(() => onDismiss?.(), linger);
    return () => clearTimeout(t);
  }, [message, tone, onDismiss]);

  if (!message) return null;
  const toneClass = tone === 'error' ? styles.toastError : tone === 'warn' ? styles.toastWarn : styles.toastOk;
  return (
    <div className={`${styles.toast} ${toneClass}`} role="status">
      {tone === 'error' || tone === 'warn' ? <AlertCircle size={17} /> : <Check size={17} />}
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      <button className={styles.toastClose} onClick={() => onDismiss?.()} aria-label="Dismiss">×</button>
    </div>
  );
};

/**
 * One place for a tab's transient feedback, so every handler reports the same
 * way: `report.ok(...)` on success, `report.fail(e)` in a catch.
 */
export const useToast = () => {
  const [toast, setToast] = useState(null);
  return {
    toast,
    clear: () => setToast(null),
    ok: (message) => setToast({ message, tone: 'info' }),
    // For an action that succeeded but produced a consequence worth reading —
    // a saved allocation that exceeds what a mailbox can deliver, say. Reporting
    // those as errors would be false; reporting them as plain success would
    // hide the one thing the operator needs to know.
    warn: (message) => setToast({ message, tone: 'warn' }),
    fail: (error) => setToast({ message: error?.message || String(error), tone: 'error' }),
  };
};

export const Stat = ({ value, label, tone }) => (
  <div className={`${styles.stat} ${tone === 'danger' ? styles.statDanger : ''} ${tone === 'success' ? styles.statSuccess : ''}`}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

// Contact and enrollment states share a vocabulary, so one map covers both.
const TONE_BY_STATUS = {
  active: 'badge-high',
  running: 'badge-high',
  pending: 'badge-medium',
  paused: 'badge-medium',
  draft: 'badge-medium',
  replied: 'badge-low',
  completed: 'badge-low',
  bounced: 'badge-critical',
  failed: 'badge-critical',
  unsubscribed: 'badge-critical',
  stopped: 'badge-medium',
  archived: 'badge-medium',
};

export const StatusBadge = ({ status }) => (
  <span className={`badge ${TONE_BY_STATUS[status] || 'badge-medium'}`}>{status || 'unknown'}</span>
);

export const Field = ({ label, help, children }) => (
  <div className={styles.field}>
    {label && <label className="field-label">{label}</label>}
    {children}
    {help && <div className={styles.fieldHelp}>{help}</div>}
  </div>
);

export const Toggle = ({ label, help, checked, onChange, disabled }) => (
  <div className={styles.switchRow}>
    <div className={styles.switchCopy}>
      <div className={styles.switchLabel}>{label}</div>
      {help && <div className={styles.switchHelp}>{help}</div>}
    </div>
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: 18, height: 18, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  </div>
);

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** "3 days ago" / "in 2 days" — relative time for queue and inbox rows. */
export const relativeTime = (value) => {
  if (!value) return '—';
  const diff = new Date(value).getTime() - Date.now();
  const abs = Math.abs(diff);
  const units = [
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];
  for (const [unit, ms] of units) {
    if (abs >= ms) {
      const count = Math.round(abs / ms);
      const plural = count === 1 ? unit : `${unit}s`;
      return diff < 0 ? `${count} ${plural} ago` : `in ${count} ${plural}`;
    }
  }
  return diff < 0 ? 'just now' : 'any moment';
};

/** Human wording for why the scheduler is not sending right now. */
export const GATE_REASONS = {
  outside_sending_days: 'Outside the campaign’s sending days',
  outside_sending_hours: 'Outside the campaign’s sending hours',
  daily_cap_reached: 'Today’s allowance has been used up',
  waiting_for_gap: 'Spacing out the next send',
  sending_disabled: 'Sending is switched off globally',
  global_cap_reached: 'Account-wide daily cap reached',
  no_due_contacts: 'Nothing due right now',
  campaign_completed: 'Every contact has finished the sequence',
  sent: 'Sending',
  // Pooled sending adds reasons of its own. Each names something specific to
  // go and fix, because "not sending" with no explanation is the failure this
  // whole gate vocabulary exists to prevent.
  no_mailboxes_assigned: 'No mailbox is assigned to this campaign',
  no_mailbox_capacity: 'Every assigned mailbox is set to 0 a day',
  mailbox_disabled: 'Its mailbox is switched off',
  mailbox_limit_reached: 'That mailbox has hit its daily limit',
  connection_expired: 'Its Zoho login needs reconnecting',
  connection_error: 'Its Zoho login is erroring',
  no_campaign_needs_this_mailbox: 'No campaign is pointed at this mailbox',
  allocation_spent: 'This campaign has used its share of that mailbox today',
};

export const Empty = ({ icon, title, children }) => (
  <div className="empty">
    <div className={styles.checkIcon}>{icon}</div>
    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{title}</div>
    {children && <div style={{ fontSize: 13, maxWidth: 420 }}>{children}</div>}
  </div>
);
